import React, { useEffect, Suspense } from 'react';
import { Route } from 'react-router';
import { Router } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { AppContentProps } from './AppContent.interfaces';
import { useStyles } from './AppContent.styles';
import { isNewCookiesStyle } from 'app/services/env.service';
import { useUser } from 'app/shared/hooks/useUser';
import { ChunkLoadErrorBoundary } from 'app/components/ChunkLoadErrorBoundary';
import retryImport from 'app/shared/helpers/retryImport';
import lazyWithReload from 'app/shared/helpers/lazyWithReload';
import { useWindowSize } from 'app/shared/hooks/useWindowSize';
import { history } from 'app/shared/helpers/getRouterHistory';
import { RouteEnum } from 'app/shared/enums';
import { SkipLink } from 'app/components/SkipLink';
import { selectIsFirstStepModeView } from 'app/store/selectors/selectIsFirstStepModeView';
import { selectIsFirstStepModeUnknown } from 'app/store/selectors/selectIsFirstStepModeUnknown';
import { getQuotationId } from 'app/store/data/quotation';
import { selectAllowedToStartServiceChat } from 'app/store/data/user';
import isBot from 'app/shared/helpers/isBot';
import { AppMenu } from 'app/components/AppMenu';
import { ViewsSwitch } from 'app/components/ViewsSwitch';
import { wasPrerendered } from 'app/shared/helpers/isPrerendering';

import useInitCookies from 'useInitCookies';
import { Loader } from 'app/components/Loader';

const CookieBarOld = lazyWithReload(() =>
  retryImport(() => import('app/components/CookieBarOld'))
);

const AiAssistantChat = lazyWithReload(() =>
  retryImport(() => import('app/components/AiAssistantChat'))
);

export default function AppContent(props: AppContentProps) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [, height] = useWindowSize(100);
  const isFirstStepModeView = useSelector(selectIsFirstStepModeView);
  const isFirstStepModeUnknown = useSelector(selectIsFirstStepModeUnknown);
  const isPhinanceLead = window.location.pathname.includes(
    RouteEnum.PHINANCE_LEAD
  );
  const shouldDisplayAppMenu =
    !isFirstStepModeView && !isFirstStepModeUnknown && !isPhinanceLead;

  const { isLoggedIn, isOperator, id: userId } = useUser();
  const quotationId = useSelector(getQuotationId);
  const canStartServiceChat = useSelector(selectAllowedToStartServiceChat);

  const auth = async () => {
    const { getAuth } = await import('app/store/data/user');
    dispatch(getAuth());
  };

  useInitCookies();

  useEffect(() => {
    if (isBot()) {
      return;
    }

    auth();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Router history={history}>
      <SkipLink />
      {shouldDisplayAppMenu && <AppMenu />}
      <ChunkLoadErrorBoundary>
        <div
          data-inert-if-modal
          className={classes.appContent}
          style={{
            // HACK: window.innerHeight === 100vh,
            // bug in webkit: https://bugs.webkit.org/show_bug.cgi?id=141832
            minHeight: shouldDisplayAppMenu ? height - 80 : height,
          }}
        >
          {!wasPrerendered && !isBot() && isLoggedIn === undefined ? (
            <Loader />
          ) : (
            <Route component={ViewsSwitch} />
          )}
          {!isNewCookiesStyle && (
            <Suspense fallback={null}>
              <CookieBarOld />
            </Suspense>
          )}
        </div>
        {isOperator && canStartServiceChat && (
          <Suspense fallback={null}>
            <AiAssistantChat key={userId} quotationId={quotationId} />
          </Suspense>
        )}
      </ChunkLoadErrorBoundary>
    </Router>
  );
}
